import { ReactElement, Ref, useImperativeHandle, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  StripeElementChangeEvent,
  StripeElementStyle,
} from '@stripe/stripe-js';
import classNames from 'classnames';
import { useAppForm } from '../forms/appForm';

interface Props {
  // Exposes `confirm` — the card fields live in Stripe iframes, so the parent
  // has to trigger confirmCardSetup imperatively mid-submit.
  ref: Ref<NewCardFormHandle>;
  defaultName?: string;
  // When set, the postcode/country come from the checkout billing fields — hide
  // the internal postcode field and use these for the card's billing_details.
  address?: { postcode: string; country?: string | undefined } | undefined;
}

export interface NewCardFormHandle {
  /**
   * Validates the form, runs `stripe.confirmCardSetup` with the provided
   * SetupIntent client secret, and returns the resulting Stripe payment
   * method ID. Returns a null id if validation failed (field errors are
   * rendered in place) or Stripe rejected the card (error is populated).
   */
  confirm: (
    clientSecret: string
  ) => Promise<
    | { paymentMethodId: null; error: string | null }
    | { paymentMethodId: string; error: null }
  >;
}

// Stripe Elements live in iframes so cannot pick up our Tailwind classes — pass
// styles explicitly. Match the Input component's body styling so the card row
// reads as part of the same form.
const stripeElementStyle: StripeElementStyle = {
  base: {
    color: '#ffffff',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: '15px',
    '::placeholder': { color: '#b8c5d0' },
    iconColor: '#b8c5d0',
  },
  invalid: {
    color: '#fc7272',
    iconColor: '#fc7272',
  },
};

// Focus is tracked manually because the inputs are iframes — `focus-within`
// never fires on the wrapper, so the focus ring has to be driven by Stripe's
// own focus/blur events.
const StripeField = ({
  label,
  className,
  focused,
  error,
  children,
}: {
  label: string;
  className?: string;
  focused: boolean;
  error?: string | undefined;
  children: ReactElement;
}): ReactElement => (
  <label className={classNames('flex flex-col', className)}>
    <span className="mb-2 font-semibold text-body-medium">{label}</span>
    <div
      className={classNames(
        'border rounded-xs bg-background-secondary px-3 py-3 transition-all duration-100',
        {
          'outline-2 outline-white border-white': focused,
          'border-grey-t-700 hover:border-white': !focused && !error,
          'border-red-300': !!error && !focused,
        }
      )}
    >
      {children}
    </div>
    {error && <span className="mt-1 text-sm text-red-300">{error}</span>}
  </label>
);

export const NewCardForm = ({
  ref,
  defaultName = '',
  address,
}: Props): ReactElement => {
  const stripe = useStripe();
  const elements = useElements();

    // The card fields are Stripe Elements (Stripe owns their value), so only the
    // name/postcode text inputs live in the form; card validity is tracked
    // separately via the Stripe change events below.
    const form = useAppForm({
      defaultValues: {
        nameOnCard: defaultName,
        postcode: '',
      },
    });

    const [cardErrors, setCardErrors] = useState<{
      number?: string;
      expiry?: string;
      cvc?: string;
    }>({});

    const [cardComplete, setCardComplete] = useState<{
      number: boolean;
      expiry: boolean;
      cvc: boolean;
    }>({ number: false, expiry: false, cvc: false });

    const [focusedField, setFocusedField] = useState<
      'cvc' | 'expiry' | 'number' | null
    >(null);

    const focusHandlers = (field: 'cvc' | 'expiry' | 'number') => ({
      onFocus: () => setFocusedField(field),
      onBlur: () =>
        setFocusedField((current) => (current === field ? null : current)),
    });

    const handleCardChange =
      (key: 'cvc' | 'expiry' | 'number') =>
      (event: StripeElementChangeEvent) => {
        setCardErrors((prev) => ({ ...prev, [key]: event.error?.message }));

        setCardComplete((prev) => ({ ...prev, [key]: event.complete }));
      };

    useImperativeHandle(ref, () => ({
      confirm: async (clientSecret) => {
        if (!stripe || !elements) {
          return { paymentMethodId: null, error: 'Stripe is not ready.' };
        }

        const cardNumber = elements.getElement(CardNumberElement);

        if (!cardNumber) {
          return { paymentMethodId: null, error: 'Card field not mounted.' };
        }

        const missing: typeof cardErrors = {};

        if (!cardComplete.number)
          missing.number = cardErrors.number ?? 'Card number is incomplete.';
        if (!cardComplete.expiry)
          missing.expiry = cardErrors.expiry ?? 'Expiry is incomplete.';
        if (!cardComplete.cvc)
          missing.cvc = cardErrors.cvc ?? 'CVV is incomplete.';

        setCardErrors((prev) => ({ ...prev, ...missing }));

        // Validate the name/postcode fields; errors render in place via the
        // subscribed form.Field inputs below.
        const fieldErrors = await form.validateAllFields('submit');

        if (
          missing.number ||
          missing.expiry ||
          missing.cvc ||
          fieldErrors.length > 0
        ) {
          return { paymentMethodId: null, error: null };
        }

        const { nameOnCard } = form.state.values;

        // The postcode/country come from the billing-details fields at checkout,
        // or the internal postcode field when adding a card standalone.
        const postcode = address?.postcode ?? form.state.values.postcode;

        // confirmCardSetup creates AND attaches the card to the customer — the
        // secure attach path (you can only attach a card you hold the details
        // for, bound to this customer's SetupIntent).
        const { setupIntent, error: setupError } =
          await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardNumber,
              billing_details: {
                name: nameOnCard,
                address: {
                  postal_code: postcode,
                  ...(address?.country ? { country: address.country } : {}),
                },
              },
            },
          });

        if (setupError) {
          return {
            paymentMethodId: null,
            error: setupError.message ?? 'Could not save card.',
          };
        }

        const paymentMethodId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : (setupIntent.payment_method?.id ?? null);

        if (!paymentMethodId) {
          return { paymentMethodId: null, error: 'Could not save card.' };
        }

        return { paymentMethodId, error: null };
      },
    }));

    return (
      <div className="flex flex-col gap-4">
        <form.AppField
          name="nameOnCard"
          validators={{
            onSubmit: ({ value }) => (value.trim() ? undefined : 'Required'),
          }}
          children={(field) => <field.TextField label="Name on card" />}
        />
        <StripeField
          label="Card number"
          focused={focusedField === 'number'}
          error={cardErrors.number}
        >
          <CardNumberElement
            options={{ style: stripeElementStyle, showIcon: true }}
            onChange={handleCardChange('number')}
            {...focusHandlers('number')}
          />
        </StripeField>
        <div className="grid grid-cols-2 gap-3">
          <StripeField
            label="Expiry date"
            focused={focusedField === 'expiry'}
            error={cardErrors.expiry}
          >
            <CardExpiryElement
              options={{ style: stripeElementStyle }}
              onChange={handleCardChange('expiry')}
              {...focusHandlers('expiry')}
            />
          </StripeField>
          <StripeField
            label="CVV"
            focused={focusedField === 'cvc'}
            error={cardErrors.cvc}
          >
            <CardCvcElement
              options={{ style: stripeElementStyle }}
              onChange={handleCardChange('cvc')}
              {...focusHandlers('cvc')}
            />
          </StripeField>
        </div>
        {!address && (
          <div className="max-w-45">
            <form.AppField
              name="postcode"
              validators={{
                onSubmit: ({ value }) =>
                  value.trim() ? undefined : 'Required',
              }}
              children={(field) => <field.TextField label="Postcode" />}
            />
          </div>
        )}
      </div>
    );
};
